import { Checkbox, FormControlLabel, Grid, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { memo, useCallback, useEffect, useState } from "react";
import { OverrideRow } from "./OverrideRow";
import {
  INVALID_TIME_FORMAT,
  START_TIME_MUST_BE_BEFORE_END_TIME,
  END_TIME_MUST_BE_AFTER_START_TIME,
  START_TIME_MUST_BE_BEFORE_MIN_TO_MIDNIGHT,
  END_TIME_MUST_BE_AFTER_MIDNIGHT,
} from "../../../../consts/strings";
import {
  DEFAULT_OVERRIDE_ROW,
  OVERRIDE_TYPES_KEYS,
} from "../../../../consts/overrides";
import {
  isHHMMFormat,
  timestampToHHMM,
  getTimestampFromHHMM,
  isTimeGreaterBy,
} from "../../../../utils/date";
import {
  getRecalculatedOverride,
  clearOverrideStartTimes,
  clearOverrideEndTimes,
} from "../../../../utils/overrides";

const useStyles = makeStyles({
  container: {
    minHeight: "60px",
  },
  label: {
    marginLeft: 0,
  },
});

export const SingleOverride = memo(
  ({ override, onChange, onValidated, disabled = false }) => {
    const styles = useStyles();
    const [firstRow, setFirstRow] = useState({ ...DEFAULT_OVERRIDE_ROW });
    const [firstRowError, setFirstRowError] = useState(null);
    const [secondRow, setSecondRow] = useState({ ...DEFAULT_OVERRIDE_ROW });
    const [secondRowError, setSecondRowError] = useState(null);
    const [excludeOneHour, setExcludeOneHour] = useState(false);
    const [showSecondRow, setShowSecondRow] = useState(false);

    useEffect(() => {
      const {
        uiOverrideStartTime,
        uiOverrideEndTime,
        startOperator,
        endOperator,
        excludeOneHour,
      } = override;
      const startRow = {
        operatorType: startOperator,
        selectTime: timestampToHHMM(uiOverrideStartTime) || "",
        selectType: OVERRIDE_TYPES_KEYS.startTime,
      };
      const endRow = {
        operatorType: endOperator,
        selectTime: timestampToHHMM(uiOverrideEndTime) || "",
        selectType: OVERRIDE_TYPES_KEYS.endTime,
      };
      setFirstRow(
        !!uiOverrideStartTime || !uiOverrideEndTime ? startRow : endRow
      );

      const hasBothRows = !!uiOverrideStartTime && !!uiOverrideEndTime;
      setShowSecondRow(hasBothRows);
      hasBothRows && setSecondRow(endRow);
      setExcludeOneHour(!!excludeOneHour);
    }, [override]);

    useEffect(() => {
      if (!onValidated) return;

      const minServiceWindow = excludeOneHour ? 61 : 30;

      if (firstRow.selectTime && !isHHMMFormat(firstRow.selectTime))
        firstRowError !== INVALID_TIME_FORMAT &&
          setFirstRowError(INVALID_TIME_FORMAT);
      else if (
        firstRow.selectType === OVERRIDE_TYPES_KEYS.startTime &&
        !showSecondRow &&
        firstRow.selectTime &&
        !isTimeGreaterBy(firstRow.selectTime, "23:59", minServiceWindow)
      ) {
        const error =
          START_TIME_MUST_BE_BEFORE_MIN_TO_MIDNIGHT(minServiceWindow);
        firstRowError !== error && setFirstRowError(error);
      } else if (
        firstRow.selectType === OVERRIDE_TYPES_KEYS.endTime &&
        !showSecondRow &&
        firstRow.selectTime &&
        !isTimeGreaterBy("00:00", firstRow.selectTime, minServiceWindow)
      ) {
        const error = END_TIME_MUST_BE_AFTER_MIDNIGHT(minServiceWindow);
        firstRowError !== error && setFirstRowError(error);
      } else if (
        [firstRow.selectTime, secondRow.selectTime].every(isHHMMFormat) &&
        !isTimeGreaterBy(
          firstRow.selectTime,
          secondRow.selectTime,
          minServiceWindow
        )
      ) {
        const error = START_TIME_MUST_BE_BEFORE_END_TIME(minServiceWindow);
        firstRowError !== error && setFirstRowError(error);
      } else if (firstRowError !== null) setFirstRowError(null);

      if (secondRow.selectTime && !isHHMMFormat(secondRow.selectTime))
        secondRowError !== INVALID_TIME_FORMAT &&
          setSecondRowError(INVALID_TIME_FORMAT);
      else if (
        [firstRow.selectTime, secondRow.selectTime].every(isHHMMFormat) &&
        !isTimeGreaterBy(
          firstRow.selectTime,
          secondRow.selectTime,
          minServiceWindow
        )
      ) {
        const error = END_TIME_MUST_BE_AFTER_START_TIME(minServiceWindow);
        secondRowError !== error && setSecondRowError(error);
      } else if (secondRowError !== null) setSecondRowError(null);
    }, [
      onValidated,
      firstRow,
      firstRowError,
      secondRow,
      secondRowError,
      excludeOneHour,
      showSecondRow,
    ]);

    useEffect(() => {
      if (!override || !onValidated) return;
      const { overrideUuid } = override;
      if (
        firstRowError ||
        secondRowError ||
        !isRowComplete(firstRow) ||
        (showSecondRow && !isRowComplete(secondRow))
      )
        onValidated({ overrideUuid, isValid: false });
      else onValidated({ overrideUuid, isValid: true });
    }, [
      onValidated,
      override,
      firstRow,
      firstRowError,
      secondRow,
      secondRowError,
      showSecondRow,
    ]);

    const onFirstRowChange = useCallback(
      ({ operatorType, selectTime, selectType }) => {
        const selectTypeChanged = firstRow.selectType !== selectType;
        setFirstRow({ operatorType, selectTime, selectType });
        if (!isRowComplete({ operatorType, selectTime })) return;

        const isStartTime = selectType === OVERRIDE_TYPES_KEYS.startTime;
        const isEndTime = selectType === OVERRIDE_TYPES_KEYS.endTime;
        const updatedOverride = getRecalculatedOverride({
          ...override,
          [isStartTime ? "startOperator" : "endOperator"]: operatorType,
          [isStartTime ? "uiOverrideStartTime" : "uiOverrideEndTime"]:
            getTimestampFromHHMM(selectTime),
          excludeOneHour,
        });

        if (selectTypeChanged && isStartTime)
          clearOverrideEndTimes(updatedOverride);
        else if (selectTypeChanged && isEndTime)
          clearOverrideStartTimes(updatedOverride);

        onChange(updatedOverride);
      },
      [onChange, override, firstRow, excludeOneHour]
    );

    const onSecondRowChange = useCallback(
      ({ operatorType, selectTime, selectType }) => {
        setSecondRow({ operatorType, selectTime, selectType });
        if (!isRowComplete({ operatorType, selectTime })) return;

        const updatedOverride = getRecalculatedOverride({
          ...override,
          endOperator: operatorType,
          uiOverrideEndTime: getTimestampFromHHMM(selectTime),
          excludeOneHour,
        });
        onChange(updatedOverride);
      },
      [onChange, override, excludeOneHour]
    );

    const onCheckboxChange = useCallback(
      (excludeOneHour) => {
        setExcludeOneHour(excludeOneHour);
        if (
          isRowComplete(firstRow) &&
          (!showSecondRow || isRowComplete(secondRow))
        )
          onChange({ ...override, excludeOneHour });
      },
      [onChange, override, showSecondRow, firstRow, secondRow]
    );

    const isRowComplete = (row) =>
      row.operatorType && isHHMMFormat(row.selectTime);

    const handlePlusClick = useCallback(() => {
      const { startTime, endTime } = OVERRIDE_TYPES_KEYS;
      if (firstRow.selectType === startTime)
        setSecondRow({ selectType: endTime });
      else {
        setFirstRow({ ...DEFAULT_OVERRIDE_ROW, selectType: startTime });
        setSecondRow({ ...firstRow });
      }
      setShowSecondRow(true);
    }, [firstRow]);

    const handleMinusClick = useCallback(() => {
      setSecondRow({ ...DEFAULT_OVERRIDE_ROW });
      setShowSecondRow(false);
      const updatedOverride = { ...override, excludeOneHour };
      if (isRowComplete(firstRow)) clearOverrideEndTimes(updatedOverride);
      else clearOverrideStartTimes(updatedOverride);
      onChange(updatedOverride);
    }, [onChange, override, firstRow, excludeOneHour]);

    return (
      <Grid
        container
        direction={"column"}
        spacing={2}
        className={styles.container}
      >
        <Grid
          item
          style={
            !!firstRowError && showSecondRow ? { marginBottom: "28px" } : {}
          }
        >
          <OverrideRow
            showMinus={true}
            disableSelectType={disabled || showSecondRow}
            disableAdd={showSecondRow || !isRowComplete(firstRow)}
            disableMinus={true}
            row={firstRow}
            error={firstRowError}
            onPlusClick={handlePlusClick}
            onChange={onFirstRowChange}
            disabled={disabled}
          />
        </Grid>
        {showSecondRow && (
          <Grid item>
            <OverrideRow
              showMinus={true}
              row={secondRow}
              error={secondRowError}
              onChange={onSecondRowChange}
              disableSelectType={true}
              disableAdd={true}
              onMinusClick={handleMinusClick}
              disabled={disabled}
            />
          </Grid>
        )}
        <Grid item>
          <FormControlLabel
            label={
              <Typography>Exclude One Hour Or Less Service Windows</Typography>
            }
            className={styles.label}
            control={
              <Checkbox
                checked={excludeOneHour}
                color={"default"}
                onChange={(e) => onCheckboxChange(e.target.checked)}
                disabled={disabled}
              />
            }
          />
        </Grid>
      </Grid>
    );
  }
);
